/**
 * Shared asset-creation constants + helpers.
 *
 * Single source of truth for the asset-type option lists, the session→asset-type
 * default map, the AS/NZS test-frequency map, and per-type element/component/item
 * defaults. Consumed by AddAssetModal (desktop) and AddAssetSmartForm (mobile) so
 * the two creation surfaces never drift apart on compliance-critical defaults.
 *
 * CONDITION_RATINGS is re-exported from constants/conditionRatings.js (the canonical
 * scale) so callers have one import for everything the create form needs.
 */
import { CONDITION_RATINGS } from '../constants/conditionRatings';

export { CONDITION_RATINGS };

export const ELECTRICAL_ASSET_TYPES = [
  { value: 'exit_light', label: 'Exit Light' },
  { value: 'emergency_light', label: 'Emergency Light' },
  { value: 'rcd', label: 'RCD' },
  { value: 'distribution_board', label: 'Distribution Board' },
  { value: 'duress_button', label: 'Duress Button' },
  { value: 'ceiling_fan', label: 'Ceiling Fan' },
  { value: 'wall_heater', label: 'Wall Heater' },
  { value: 'automated_gate', label: 'Automated Gate' },
  { value: 'external_lighting_control', label: 'External Lighting Control' },
  { value: 'emergency_light_test_kit', label: 'Emergency Light Test Kit' },
];

export const FITMENTS_ASSET_TYPES = [
  { value: 'workshop_equipment', label: 'Workshop Equipment' },
];

/** Default individual asset type from a session's combined/grouped asset type. */
export const SESSION_TYPE_TO_ASSET_TYPE = {
  combined_exit_emergency: 'exit_light',
  combined_db_rcd: 'rcd',
  combined_fan_heater: 'ceiling_fan',
  duress_button: 'duress_button',
  automated_gate: 'automated_gate',
  external_lighting_control: 'external_lighting_control',
  workshop_equipment: 'workshop_equipment',
  emergency_light_test_kit: 'emergency_light_test_kit',
};

/** Asset types identified by their position on a board rather than by a code. */
const BOARD_CIRCUIT_ASSET_TYPES = ['rcd', 'distribution_board'];

/**
 * Does this asset type carry board/circuit identity (DB number, CB number, capacity)?
 *
 * Boards and RCDs are identified by where they sit on a board. Every other type is
 * identified by its equipment code — an exit light has no circuit breaker, and asking
 * for one leaves it with no usable identifier. Both creation surfaces (AddAssetModal
 * on desktop, AddAssetSmartForm on mobile) consume this so they cannot drift on which
 * identity fields a type actually has.
 */
export function usesBoardCircuitFields(assetType) {
  return BOARD_CIRCUIT_ASSET_TYPES.includes(assetType);
}

/**
 * Does this asset type carry a circuit breaker number?
 *
 * Only an RCD sits on a circuit within a board, so only an RCD has a circuit
 * breaker number. A distribution board IS the board — its identity is the
 * db_number alone, and giving it a circuit_breaker_number makes it display as
 * "<board> - <board>" and group as a circuit nested inside itself. This is the
 * choke point that keeps that field off boards (and every non-RCD type) even
 * when a form leaves a typed value in state after a type switch.
 */
export function usesCircuitBreakerNumber(assetType) {
  return assetType === 'rcd';
}

/** Test frequency in months per asset type (mirrors backend). */
export const ASSET_FREQUENCY_MAP = {
  exit_light: 6,
  emergency_light: 6,
  rcd: 12,
  distribution_board: 12,
  ceiling_fan: 12,
  wall_heater: 12,
  external_lighting_control: 12,
  duress_button: 12,
  automated_gate: 6,
  workshop_equipment: 12,
  emergency_light_test_kit: 6,
};

/** Auto-filled element / component / item_type per asset type. */
export const ASSET_TYPE_DEFAULTS = {
  exit_light:                { element: 'Electrical System', component: 'Emergency Lighting',         itemType: 'EXIT Sign Running Man Light' },
  emergency_light:           { element: 'Electrical System', component: 'Emergency Lighting',         itemType: 'Emergency Light (Built-in Module)' },
  rcd:                       { element: 'Electrical System', component: 'Power',                      itemType: 'RCD / Safety Switch' },
  duress_button:             { element: 'Electrical System', component: 'Access System',              itemType: 'Panic / Duress Button' },
  distribution_board:        { element: 'Electrical System', component: 'Power',                      itemType: 'Distribution Board' },
  ceiling_fan:               { element: 'Electrical System', component: 'Fans & Heaters',             itemType: 'Ceiling Fan' },
  wall_heater:               { element: 'Electrical System', component: 'Fans & Heaters',             itemType: 'Wall Heater' },
  automated_gate:            { element: 'Electrical System', component: 'Access System',              itemType: 'Automated Gate' },
  external_lighting_control: { element: 'Electrical System', component: 'External Security Lighting', itemType: 'External Lighting Control' },
  workshop_equipment:        { element: 'Fitments',          component: 'Workshop Equipment',         itemType: '' },
  emergency_light_test_kit:  { element: 'Electrical System', component: 'Emergency Lighting',        itemType: 'Emergency Light Test Kit' },
};

/** Build a blank create-asset form, seeded with per-type defaults. */
export function buildInitialFormData(elementType, sessionAssetType) {
  const assetType =
    SESSION_TYPE_TO_ASSET_TYPE[sessionAssetType] ||
    (elementType === 'fitments' ? 'workshop_equipment' : 'exit_light');
  const defaults = ASSET_TYPE_DEFAULTS[assetType] || {};
  return {
    assetType,
    building: '',
    room: '',
    equipmentCode: '',
    dbNumber: '',
    circuitBreakerNumber: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    installationDate: '',
    testFrequencyMonths: ASSET_FREQUENCY_MAP[assetType] || 12,
    conditionRating: '',
    capacity: '',
    element: defaults.element || '',
    component: defaults.component || '',
    itemType: defaults.itemType || '',
    notes: '',
  };
}

/** Map an asset-type change to the fields it drives (frequency + element defaults). */
export function assetTypeChangePatch(type) {
  const defaults = ASSET_TYPE_DEFAULTS[type] || {};
  return {
    assetType: type,
    testFrequencyMonths: ASSET_FREQUENCY_MAP[type] || 12,
    element: defaults.element || '',
    component: defaults.component || '',
    itemType: defaults.itemType || '',
  };
}

/**
 * Build the POST /assets payload from form data + locked session context.
 *
 * Identity fields are gated both ways by asset type, because both forms hide the
 * inputs on a type switch but keep the typed values in state. Without the gate an
 * exit light created after visiting a board would carry that board's number into
 * `db_number`, and an RCD created after visiting an exit-light group would carry
 * that light's code into `equipment_code`.
 */
export function buildAssetPayload(formData, { siteId, elementType }) {
  const boardCircuit = usesBoardCircuitFields(formData.assetType);
  const hasCircuitBreaker = usesCircuitBreakerNumber(formData.assetType);
  return {
    siteId: parseInt(siteId, 10),
    assetType: formData.assetType,
    element_type: elementType,
    building: formData.building || undefined,
    room: formData.room || undefined,
    equipmentCode: (!boardCircuit && formData.equipmentCode) || undefined,
    dbNumber: (boardCircuit && formData.dbNumber) || undefined,
    circuitBreakerNumber: (hasCircuitBreaker && formData.circuitBreakerNumber) || undefined,
    manufacturer: formData.manufacturer || undefined,
    model: formData.model || undefined,
    serialNumber: formData.serialNumber || undefined,
    installationDate: formData.installationDate || undefined,
    testFrequencyMonths: formData.testFrequencyMonths,
    conditionRating: formData.conditionRating ? parseInt(formData.conditionRating, 10) : undefined,
    capacity: (boardCircuit && formData.capacity) || undefined,
    element: formData.element || undefined,
    component: formData.component || undefined,
    itemType: formData.itemType || undefined,
    notes: formData.notes || undefined,
  };
}
